// The shared page's requests. Deliberately NOT the shared axios instance: its
// request interceptor rejects when nobody is signed in, and its 401/403 handler
// clears the session, which would sign out a recruiter who opens a revoked link.
import type { CallMedia, CallPageData } from "./types";

const API_URL = `${import.meta.env.VITE_BACKEND_URL}` || "";

/** The link is gone: expired, stopped, deleted, or never valid. The server says nothing more. */
export class SharedLinkGone extends Error {
  constructor() {
    super("This link has expired or is no longer shared.");
  }
}

export type SharedCallPageData = CallPageData & { shared: { expires_at: string } };

async function post(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}/shared_call_page`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "omit",
      referrerPolicy: "no-referrer",
    });
  } catch {
    throw new Error("Could not reach Dooglie. Check your connection and try again.");
  }
  if (response.status === 404) throw new SharedLinkGone();
  if (response.status === 429) throw new Error("Too many people are opening this link. Try again in a moment.");
  if (!response.ok) throw new Error("Could not load this call.");
  return (await response.json()) as Record<string, unknown>;
}

export async function getSharedCallPage(token: string): Promise<SharedCallPageData> {
  return (await post({ token })) as unknown as SharedCallPageData;
}

export async function getSharedCallMedia(token: string): Promise<CallMedia> {
  const data = await post({ token, media_only: true });
  return data.media as CallMedia;
}
